import { handleActions } from 'redux-actions';
import moment from 'moment';

import createCalendar, { getDateRangeWithTimeZoneFromCalendar } from '../shared/calendar';
import createFilters, { getFiltersObject } from '../shared/filters';
import createPagination from '../shared/new-pagination';
import http from '../shared/http';

import { utmTagFilters } from '../shared/filters/list';

import endpoints from '../../config/endpoints';

export const namespace = 'registrationAnalytics';

const calendar = createCalendar({
	namespace,
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace,
	stateNodeName: 'filters',
	listOfFilters: [
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'managers',
			title: 'Managers',
			placeholder: 'Add managers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS
			}
		},
		{
			name: 'decisions',
			title: 'Decisions',
			placeholder: 'Add decisions',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'provided',
				source: [
					{ value: 'approved', label: '✅ Approved' },
					{ value: 'rejected', label: '❌ Rejected' }
				]
			}
		},
		{
			name: 'sources',
			title: 'Sources',
			placeholder: 'Add sources',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.affiliate-registration-sources'
			}
		},
		{
			name: 'tags',
			title: 'Tags',
			placeholder: 'Add affiliate\'s tag',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_TAGS
			}
		},
		{
			name: 'is_active',
			title: 'Active',
			placeholder: 'Is active?',
			value: [],
			multi: false,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: [
					{ value: '+', label: '✅ Active' },
					{ value: '-', label: '❌ Not Active' }
				]
			}
		}
	].concat(utmTagFilters)
});

const pagination = createPagination({
	namespace,
	initialState: { perPage: 50 }
});

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const FETCH_MORE_SUCCESS = `${namespace}/fetch-more-success`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;
const APPLY = `${namespace}/apply`;
const CLEAR = `${namespace}/clear`;

// initial state
const initialState = {
	isLoading: true,
	isError: false,
	data: null,
	filters: filters.initialState,
	calendar: calendar.initialState,
	pagination: pagination.initialState
};

// actions
export const actions = {
	own: {
		fetchData: () => (dispatch, getState) => {
			const page = 1;
			request(getRequestQuery(page, getState), FETCH_DATA_SUCCESS)(dispatch)
				.then((action) => {
					if (action.type === FETCH_DATA_SUCCESS) {
						dispatch(pagination.actions.update({
							page,
							total: action.payload.data.total
						}));
					}
				});
		},
		fetchMore: () => (dispatch, getState) => {
			const page = getState()[namespace].pagination.currentPage + 1;
			request(getRequestQuery(page, getState), FETCH_MORE_SUCCESS)(dispatch)
				.then((action) => {
					if (action.type === FETCH_MORE_SUCCESS) {
						dispatch(pagination.actions.update({
							page,
							total: action.payload.data.total
						}));
					}
				});
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR })
	},
	apply: () => (dispatch, getState) => {
		dispatch({ type: APPLY });
		dispatch(actions.own.fetchData());
	},
	clear: () => (dispatch, getState) => {
		dispatch({ type: CLEAR });
		dispatch(actions.own.fetchData());
	},
	filters: filters.actions,
	calendar: calendar.actions,
	pagination: {
		showMore: () => (dispatch, getState) => {
			dispatch(actions.own.toggleProgressBar());
			dispatch(actions.own.fetchMore());
		}
	}
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			isLoading: false,
			isError: false
		}),
		[FETCH_MORE_SUCCESS]: (state, action) => ({
			...state,
			data: state.data.concat(action.payload.data.rows),
			isLoading: false,
			isError: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			isLoading: false,
			isError: true
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		}),
		[APPLY]: (state, action) => ({
			...state,
			data: null,
			isLoading: true
		}),
		[CLEAR]: (state, action) => initialState
	},
	filters.reducers,
	calendar.reducers,
	pagination.reducers
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getFilters: state => filters.selectors.getFilters(
		state => state[namespace].filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state[namespace].calendar,
		state => state
	)(state),
	getPagination: pagination.selectors.getPagination
};

function getRequestQuery(page, getState) {
	const state = getState()[namespace];

	const limit = state.pagination.perPage;
	const range = getDateRangeWithTimeZoneFromCalendar(state.calendar);
	const filters = getFiltersObject(state.filters, false);
	const inverse_filters = getFiltersObject(state.filters, true);

	return {
		offset: (page - 1) * limit,
		limit,
		dates: [range.start, range.end],
		filters,
		inverse_filters
	};
}

function request(query, successActionType) {
	return http({
		url: 'v2/registration-analytics',
		method: 'get',
		actions: [successActionType, FETCH_DATA_ERROR],
		query
	});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/registration-analytics/index.js